<script lang="ts" setup>
import { CheckIcon, DocumentDuplicateIcon, LinkIcon, PlusIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import KnocSearch from "./KnocSearch.vue";
import ReachExpander from "./ReachExpander.vue";
import Tooltip from "./ui/Tooltip.vue";
import UserAgentDisplay from "./ui/UserAgentDisplay.vue";
import { useThemeStore } from "../pinia/theme";
import { useAgenticAccessStore, type AgenticAccess } from "../pinia/agenticAccess";
import { useAuthStore, type ACL } from "../pinia/auth";
import {
  agenticGrantStatusChipClass,
  agenticGrantStatusKind,
  agenticGrantStatusLabelKey,
  agenticGrantStatusUsable,
} from "../shared/services/agenticGrantStatus";
import { countryCodeToFlag } from "../utils/countryCodeToFlag";
import { formatCountdown } from "../utils/formatCountdown";
import api from "../services/api";
import { getServerOrigin } from "../capacitor/server-url";

const COPY_FEEDBACK_MS = 2000;
const POLL_INTERVAL_MS = 5000;
const TICK_INTERVAL_MS = 1000;
const DURATION_OPTIONS = [
  { value: 5 * 60, key: "agenticAccessPanel.duration.min5" },
  { value: 10 * 60, key: "agenticAccessPanel.duration.min10" },
  { value: 15 * 60, key: "agenticAccessPanel.duration.min15" },
  { value: 60 * 60, key: "agenticAccessPanel.duration.hour1" },
  { value: 4 * 60 * 60, key: "agenticAccessPanel.duration.hours4" },
  { value: 8 * 60 * 60, key: "agenticAccessPanel.duration.hours8" },
  { value: 24 * 60 * 60, key: "agenticAccessPanel.duration.hours24" },
];

const props = defineProps<{
  acls: ACL[];
}>();

const emit = defineEmits<{ summary: [{ count: number; hasPending: boolean }] }>();

const { t } = useI18n();
const themeStore = useThemeStore();
const theme = computed(() => themeStore.accent);
const highContrast = computed(() => themeStore.highAccessibility);
const authStore = useAuthStore();
const eaStore = useAgenticAccessStore();

const showCreateForm = ref(false);
const name = ref("");
const selectedDuration = ref(DURATION_OPTIONS.find((o) => o.key === "agenticAccessPanel.duration.hour1")!.value);
const selectedAclIds = ref<string[]>([]);
const isCreating = ref(false);
const createError = ref<string | null>(null);
// Per-agent action error, keyed by agent id, so a failed approve/revoke/copy
// surfaces on the specific card it was triggered from, not a shared banner.
const agentErrors = ref<Record<string, string>>({});
const linkCopied = ref(false);
const copiedAgentId = ref<string | null>(null);
const now = ref(Date.now());

// Capability only: offer any agentic-capable knoc, grant or not.
const eligibleAcls = computed(() => props.acls.filter((acl) => acl.PersistentAndAgenticEnabled));

const canCreate = computed(() => selectedAclIds.value.length > 0 && name.value.trim().length > 0);

// Visible agents: exclude revoked and fully expired
const visibleAgents = computed(() =>
  eaStore.agents.filter((ea) => {
    if (ea.status === "revoked") return false;
    if (ea.status === "approved" && ea.expiresAt && new Date(ea.expiresAt).getTime() < now.value) return false;
    if (ea.status === "pending_link" && new Date(ea.linkExpiresAt).getTime() < now.value) return false;
    return true;
  }),
);

const hasPending = computed(() =>
  visibleAgents.value.some((ea) => ea.status === "pending_link" || ea.status === "pending_approval"),
);

// Keep the parent tab pill / status dot in sync.
watch(
  [visibleAgents, hasPending],
  () => emit("summary", { count: visibleAgents.value.length, hasPending: hasPending.value }),
  { immediate: true, deep: true },
);

// IP flag lookup
const ipFlags = ref<Record<string, string>>({});

async function lookupFlags(ips: string[]) {
  const uncached = ips.filter((ip) => ip && !ipFlags.value[ip]);
  if (uncached.length === 0) return;
  try {
    const res = await api.post<Record<string, { country_code?: string }>>("/ipinfo", { ips: uncached });
    for (const [ip, info] of Object.entries(res.data)) {
      if (info?.country_code) {
        ipFlags.value[ip] = countryCodeToFlag(info.country_code);
      }
    }
  } catch {
    // Flags are best-effort
  }
}

async function handleCreate() {
  if (!canCreate.value || isCreating.value) return;
  isCreating.value = true;
  createError.value = null;

  try {
    const agent = await eaStore.createAgent({
      name: name.value.trim(),
      durationSeconds: selectedDuration.value,
      aclIDs: selectedAclIds.value,
    });

    if (agent.url) {
      // A relative url means the server has no PublicURL set; resolve it against
      // the server origin (not the WebView's https://localhost on native).
      const fullUrl = agent.url.startsWith("http") ? agent.url : `${await getServerOrigin()}${agent.url}`;
      try {
        await navigator.clipboard.writeText(fullUrl);
        linkCopied.value = true;
        setTimeout(() => (linkCopied.value = false), COPY_FEEDBACK_MS);
      } catch {
        // Clipboard unavailable (non-HTTPS or permission denied): link was still created
      }
    }

    name.value = "";
    showCreateForm.value = false;
  } catch (error: any) {
    const errKey = error?.response?.data?.Err;
    if (errKey && errKey.startsWith("err.")) {
      createError.value = t(errKey);
    } else {
      createError.value = error?.message || t("err.internalServerError");
    }
  } finally {
    isCreating.value = false;
  }
}

// Per-agent action lock so a double-tap on Approve/Revoke does not fire two requests.
const inFlightAgents = ref(new Set<string>());

function actionInFlight(id: string): boolean {
  return inFlightAgents.value.has(id);
}

// A grant for this IP (any source) is still being torn down on this account.
// Approving mid-revoke would race the in-flight revoke, so the approval waits
// it out. The status clears within seconds once the agent acknowledges and
// both this panel and the session status poll.
function ipRevoking(ip: string | undefined): boolean {
  if (!ip) return false;
  return props.acls.some((acl) =>
    (acl.PersistentIPs ?? []).some((c) => c.IP === ip && agenticGrantStatusKind(c.Status) === "revoking"),
  );
}

async function handleApprove(ea: AgenticAccess) {
  if (inFlightAgents.value.has(ea.id) || ea.ipError || ipRevoking(ea.ip)) return;
  delete agentErrors.value[ea.id];
  inFlightAgents.value.add(ea.id);
  try {
    // Echo the IP shown on this card; the server refuses if it changed since.
    await eaStore.approveAgent(ea.id, ea.ip ?? "");
    authStore.fetchStatus({ force: true });
  } catch (error: any) {
    // Surface the server's specific reason (e.g. the agent's IP is flagged as
    // malicious / on a block list) rather than a generic failure, on this card.
    const errKey = error?.response?.data?.Err;
    agentErrors.value[ea.id] = errKey && errKey.startsWith("err.") ? t(errKey) : t("agenticAccessPanel.approveError");
    await eaStore.fetchAgents();
  } finally {
    inFlightAgents.value.delete(ea.id);
  }
}

// Id of the agent whose revoke is awaiting an inline "are you sure?" confirmation.
const confirmingRevokeId = ref<string | null>(null);

function requestRevoke(ea: AgenticAccess) {
  if (inFlightAgents.value.has(ea.id)) return;
  confirmingRevokeId.value = ea.id;
}

function cancelRevoke() {
  confirmingRevokeId.value = null;
}

async function handleRevoke(ea: AgenticAccess) {
  if (inFlightAgents.value.has(ea.id)) return;
  confirmingRevokeId.value = null;
  delete agentErrors.value[ea.id];
  inFlightAgents.value.add(ea.id);
  try {
    await eaStore.revokeAgent(ea.id);
    authStore.fetchStatus({ force: true });
  } catch {
    agentErrors.value[ea.id] = t("agenticAccessPanel.revokeError");
    await eaStore.fetchAgents();
  } finally {
    inFlightAgents.value.delete(ea.id);
  }
}

async function handleCopyLink(ea: AgenticAccess) {
  if (!ea.url) return;
  const fullUrl = ea.url.startsWith("http") ? ea.url : `${await getServerOrigin()}${ea.url}`;
  try {
    await navigator.clipboard.writeText(fullUrl);
    copiedAgentId.value = ea.id;
    setTimeout(() => {
      if (copiedAgentId.value === ea.id) copiedAgentId.value = null;
    }, COPY_FEEDBACK_MS);
  } catch {
    agentErrors.value[ea.id] = t("agenticAccessPanel.copyError");
  }
}

// Live grant status of an approved agent collapsed to one chip. Partial
// success reads as an orange "Granted n of N" rather than the worst failure,
// one blocked knoc out of four shouldn't paint the whole card red. Until the
// first grant lands the chip counts pending knocs instead ("Pending n of N");
// once anything is granted, only granted knocs count.
const cardChips = computed(() => {
  const chips = new Map<string, { label: string; params?: Record<string, number>; cls: string }>();
  for (const ea of visibleAgents.value) {
    const chip = cardChip(ea);
    if (chip) chips.set(ea.id, chip);
  }
  return chips;
});

function cardChip(ea: AgenticAccess): { label: string; params?: Record<string, number>; cls: string } | undefined {
  if (ea.status !== "approved") return undefined;
  const statuses = ea.aclStatuses ?? [];
  if (statuses.length === 0) return undefined;

  const total = ea.aclIDs.length;
  const usable = statuses.filter((s) => agenticGrantStatusUsable(s.status)).length;

  if (usable === 0) {
    const kind = ea.grantStatus === undefined ? "error" : agenticGrantStatusKind(ea.grantStatus);
    return { label: agenticGrantStatusLabelKey(kind), cls: agenticGrantStatusChipClass(kind) };
  }

  const active = statuses.filter((s) => agenticGrantStatusKind(s.status) === "active").length;
  const allPending = statuses.every((s) => agenticGrantStatusUsable(s.status)) && active === 0 && usable >= total;
  if (allPending) {
    // Green when everything published (just awaiting the device's fetch),
    // orange while grants are still applying, same rule as the knoc badge.
    const allPublished = statuses.every((s) => agenticGrantStatusKind(s.status) === "published");
    return {
      label: "agenticAccessPanel.status.pendingCount",
      params: { pending: total, total },
      cls: agenticGrantStatusChipClass(allPublished ? "published" : "pending"),
    };
  }
  if (active < total) {
    return {
      label: "agenticAccessPanel.status.partial",
      params: { granted: active, total },
      cls: agenticGrantStatusChipClass("pending"),
    };
  }
  return { label: "agenticAccessPanel.status.active", cls: agenticGrantStatusChipClass("active") };
}

// Approval is terminal even when every grant fails; an approved agent with no
// usable grant shows an explicit no-access state instead of an approve error.
function hasNoAccess(ea: AgenticAccess): boolean {
  if (ea.status !== "approved") return false;
  return !(ea.aclStatuses ?? []).some((s) => agenticGrantStatusUsable(s.status));
}

function countdownLabel(expiresAt: string): string {
  return formatCountdown(expiresAt, now.value, t("agenticAccessPanel.expired"));
}

// Exact local date/time the unredeemed magic link stops working.
function formatExpiry(linkExpiresAt: string): string {
  return new Date(linkExpiresAt).toLocaleString();
}

let tickInterval: ReturnType<typeof setInterval> | undefined;
let pollInterval: ReturnType<typeof setInterval> | undefined;

onMounted(async () => {
  await eaStore.fetchAgents();

  const ips = eaStore.agents.map((ea) => ea.ip).filter(Boolean) as string[];
  if (ips.length > 0) lookupFlags(ips);

  tickInterval = setInterval(() => {
    now.value = Date.now();
  }, TICK_INTERVAL_MS);
  pollInterval = setInterval(() => {
    eaStore
      .fetchAgents()
      .then(() => {
        const polled = eaStore.agents.map((ea) => ea.ip).filter(Boolean) as string[];
        if (polled.length > 0) lookupFlags(polled);
      })
      .catch(() => {});
  }, POLL_INTERVAL_MS);
});

onUnmounted(() => {
  if (tickInterval) clearInterval(tickInterval);
  if (pollInterval) clearInterval(pollInterval);
});
</script>

<template>
  <div class="space-y-4">
    <p class="text-xs text-muted-foreground">{{ t("agenticAccessPanel.description") }}</p>

    <!-- Active agents list -->
    <div v-if="visibleAgents.length > 0" class="space-y-2">
      <p class="text-sm font-medium">{{ t("agenticAccessPanel.activeAgents") }}</p>
      <div v-for="ea in visibleAgents" :key="ea.id" class="rounded-md border border-input bg-background px-3 py-2">
        <div class="flex items-center justify-between gap-2">
          <div class="flex min-w-0 flex-1 items-center gap-2">
            <span v-if="ea.ip && ipFlags[ea.ip]" class="shrink-0 text-base leading-none" aria-hidden="true">{{
              ipFlags[ea.ip]
            }}</span>
            <div class="min-w-0">
              <p class="truncate text-sm font-medium">{{ ea.name || ea.ip || t("agenticAccessPanel.unnamed") }}</p>
              <p v-if="ea.ip && ea.name" class="truncate font-mono text-xs text-muted-foreground">{{ ea.ip }}</p>
              <UserAgentDisplay
                v-if="ea.status === 'pending_approval' && ea.userAgent"
                :parsed="ea.parsedUserAgent"
                :raw="ea.userAgent"
                icons-only
                class="max-w-full truncate text-xs text-muted-foreground"
              />
            </div>
          </div>

          <!-- Status badges + actions -->
          <div class="flex shrink-0 items-center gap-2">
            <Tooltip
              v-if="ea.status === 'pending_link'"
              :text="t('agenticAccessPanel.linkExpiresAt', { time: formatExpiry(ea.linkExpiresAt) })"
            >
              <span
                class="inline-flex cursor-default items-center rounded-full border border-orange-500/20 bg-orange-500/10 px-2 py-0.5 text-xs text-orange-500"
              >
                {{ t("agenticAccessPanel.status.pendingLink") }}
              </span>
            </Tooltip>

            <button
              v-if="ea.status === 'pending_link' && ea.url"
              type="button"
              class="inline-flex h-7 items-center justify-center rounded-md px-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              :aria-label="`${t('agenticAccessPanel.copyLink')} ${ea.name || ''}`"
              @click="handleCopyLink(ea)"
            >
              <CheckIcon v-if="copiedAgentId === ea.id" class="h-4 w-4 text-green-500" aria-hidden="true" />
              <DocumentDuplicateIcon v-else class="h-4 w-4" aria-hidden="true" />
            </button>

            <span
              v-if="cardChips.get(ea.id)"
              class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs"
              :class="cardChips.get(ea.id)!.cls"
            >
              {{ t(cardChips.get(ea.id)!.label, cardChips.get(ea.id)!.params ?? {}) }}
            </span>

            <span
              v-if="ea.status === 'approved' && ea.expiresAt"
              class="inline-flex items-center rounded-full border border-green-500/20 bg-green-500/10 px-2 py-0.5 text-xs text-green-500"
            >
              {{ countdownLabel(ea.expiresAt) }}
            </span>

            <Tooltip
              v-if="ea.status === 'pending_approval'"
              :text="
                ea.ipError
                  ? t(ea.ipError, { ip: ea.ip })
                  : ipRevoking(ea.ip)
                    ? t('agenticAccessPanel.revokeInProgress')
                    : ''
              "
            >
              <button
                type="button"
                class="themed-button inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs disabled:opacity-50"
                :class="(Boolean(theme) && `theme-${theme}${highContrast ? '-hc' : ''}`) || 'theme-default'"
                :aria-label="`${t('agenticAccessPanel.approve')} ${ea.name || ea.ip || ''}`"
                :disabled="actionInFlight(ea.id) || Boolean(ea.ipError) || ipRevoking(ea.ip)"
                @click="handleApprove(ea)"
              >
                <CheckIcon class="h-3.5 w-3.5" aria-hidden="true" />
                {{ t("agenticAccessPanel.approve") }}
              </button>
            </Tooltip>

            <button
              v-if="ea.status !== 'revoked'"
              type="button"
              class="inline-flex h-7 items-center justify-center rounded-md px-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-danger disabled:opacity-50"
              :aria-label="`${t('agenticAccessPanel.revoke')} ${ea.name || ea.ip || ''}`"
              :disabled="actionInFlight(ea.id) || confirmingRevokeId === ea.id"
              @click="requestRevoke(ea)"
            >
              <XMarkIcon class="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        <ReachExpander :acl-ids="ea.aclIDs" :acls="props.acls" :acl-statuses="ea.aclStatuses" class="mt-1" />

        <p
          v-if="ea.status === 'pending_approval' && ea.ipError"
          class="mt-2 border-t border-input pt-2 text-xs text-danger"
        >
          {{ t(ea.ipError, { ip: ea.ip }) }}
        </p>

        <!-- Approval consumed the link but no grant landed (blocked, offline, error) -->
        <p v-if="hasNoAccess(ea)" class="mt-2 border-t border-input pt-2 text-xs text-danger">
          {{ t("agenticAccessPanel.noAccess") }}
        </p>

        <!-- Per-card action error (approve/revoke/copy), scoped to this agent -->
        <p v-if="agentErrors[ea.id]" role="alert" class="mt-2 border-t border-input pt-2 text-xs text-danger">
          {{ agentErrors[ea.id] }}
        </p>

        <!-- Inline revoke confirmation (replaces a native confirm dialog) -->
        <div
          v-if="confirmingRevokeId === ea.id"
          class="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-input pt-2"
        >
          <p class="text-xs text-muted-foreground">
            {{ t("agenticAccessPanel.confirmRevoke", { name: ea.name || ea.ip || t("agenticAccessPanel.unnamed") }) }}
          </p>
          <div class="flex shrink-0 items-center gap-2">
            <button
              type="button"
              class="inline-flex h-7 items-center rounded-md px-2 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
              @click="cancelRevoke"
            >
              {{ t("agenticAccessPanel.cancel") }}
            </button>
            <button
              type="button"
              class="inline-flex h-7 items-center gap-1 rounded-md border border-destructive px-2 text-xs text-danger hover:bg-destructive/10 disabled:opacity-50"
              :disabled="actionInFlight(ea.id)"
              @click="handleRevoke(ea)"
            >
              <XMarkIcon class="h-3.5 w-3.5" aria-hidden="true" />
              {{ t("agenticAccessPanel.revoke") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toggle for the creation form (hidden when the list is empty so the form shows by default) -->
    <button
      v-if="!showCreateForm && visibleAgents.length > 0"
      type="button"
      class="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      @click="showCreateForm = true"
    >
      <PlusIcon class="h-4 w-4" aria-hidden="true" />
      {{ t("agenticAccessPanel.addAnother") }}
    </button>

    <!-- Create new agent -->
    <div
      v-if="showCreateForm || visibleAgents.length === 0"
      class="space-y-3 border-t border-input pt-4"
      :class="{ 'border-t-0 pt-0': visibleAgents.length === 0 }"
    >
      <div class="flex flex-wrap items-end gap-2">
        <div>
          <label for="agentic-name" class="mb-1 block text-xs font-medium text-muted-foreground">{{
            t("agenticAccessPanel.nameLabel")
          }}</label>
          <input
            id="agentic-name"
            v-model="name"
            type="text"
            required
            :placeholder="t('agenticAccessPanel.namePlaceholder')"
            class="h-9 w-36 rounded-md border border-input bg-background px-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <div>
          <label for="agentic-duration" class="mb-1 block text-xs font-medium text-muted-foreground">{{
            t("agenticAccessPanel.durationLabel")
          }}</label>
          <select
            id="agentic-duration"
            v-model="selectedDuration"
            class="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option v-for="opt in DURATION_OPTIONS" :key="opt.value" :value="opt.value">
              {{ t(opt.key) }}
            </option>
          </select>
        </div>
      </div>

      <KnocSearch v-model="selectedAclIds" :acls="eligibleAcls" />

      <div v-if="createError" role="alert" class="rounded-md border border-destructive bg-destructive/10 p-2">
        <p class="text-xs text-danger">{{ createError }}</p>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          :disabled="!canCreate || isCreating"
          class="themed-button inline-flex h-9 items-center gap-1.5 px-4 text-sm"
          :class="(Boolean(theme) && `theme-${theme}${highContrast ? '-hc' : ''}`) || 'theme-default'"
          @click="handleCreate"
        >
          <span
            v-if="isCreating"
            aria-hidden="true"
            class="inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          ></span>
          <LinkIcon v-else class="h-3.5 w-3.5" aria-hidden="true" />
          {{ t("agenticAccessPanel.createLink") }}
        </button>
        <span v-if="linkCopied" role="status" class="text-xs text-green-500">{{ t("agenticAccessPanel.copied") }}</span>
      </div>
    </div>
  </div>
</template>
